import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BookingConfirmed } from "./components/BookingConfirmed";
import { PageWidth } from "./components/travel-ui";
import { PointsCashPay } from "./components/PointsCashPay";
import { useMarketplaceSearch } from "./context/SearchContext";
import { getHotelById } from "./data/mock-hotels";
import { formatMoney, formatLongDate } from "./lib/format";
import { clampPoints, quoteStay } from "./lib/pricing";
import { useMemberAuth } from "./auth/MemberAuthContext";
import { saveTrip } from "./services/trip.service";
import { useWallet } from "./services/wallet.service";

export default function MemberBooking() {
  const { hotelId = "" } = useParams();
  const [params] = useSearchParams();
  const { search } = useMarketplaceSearch();
  const { session } = useMemberAuth();
  const wallet = useWallet();
  const hotel = getHotelById(hotelId);
  const roomId = params.get("room") || hotel?.rooms[0]?.id || "";
  const checkIn = params.get("checkIn") || search.checkIn;
  const checkOut = params.get("checkOut") || search.checkOut;
  const guests = Number(params.get("guests") || search.guests);
  const rooms = Number(params.get("rooms") || search.rooms);
  const room = hotel?.rooms.find((item) => item.id === roomId) ?? null;
  const quote = useMemo(
    () => (room ? quoteStay(room, checkIn, checkOut, rooms) : null),
    [room, checkIn, checkOut, rooms],
  );

  const [name, setName] = useState(session?.fullName || "");
  const [email, setEmail] = useState(session?.channel === "email" ? session.contact : "");
  const [mobile, setMobile] = useState(session?.channel === "mobile" ? session.contact : "");
  useEffect(() => {
    if (!wallet.loaded) return;
    setName((value) => value || wallet.fullName);
    setEmail((value) => value || wallet.email);
    setMobile((value) => value || wallet.mobile);
  }, [wallet.loaded, wallet.fullName, wallet.email, wallet.mobile]);
  const [requests, setRequests] = useState("");
  const [points, setPoints] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<null | {
    reference: string;
    tierBefore: string;
    tierAfter: string;
    cashPaid: number;
    pointsUsed: number;
    pointsEarned: number;
    earnPending: boolean;
    earnAvailableAt: string | null;
  }>(null);

  if (!hotel || !room || !quote) {
    return (
      <PageWidth>
        <p className="font-semibold">Choose a room before checkout.</p>
        <Link to="/member/hotels" className="text-[#006ce4]">
          Back to stays
        </Link>
      </PageWidth>
    );
  }

  if (done) {
    return (
      <PageWidth>
        <BookingConfirmed
          reference={done.reference}
          title={hotel.name}
          lines={[
            room.name,
            `${formatLongDate(checkIn)} – ${formatLongDate(checkOut)}`,
            `${guests} adults · ${rooms} room${rooms === 1 ? "" : "s"}`,
            `Guest: ${name}`,
          ]}
          cashPaid={done.cashPaid}
          currency={wallet.currency}
          pointsUsed={done.pointsUsed}
          pointsEarned={done.pointsEarned}
          earnPending={done.earnPending}
          earnAvailableAt={done.earnAvailableAt}
          tierBefore={done.tierBefore}
          tierAfter={done.tierAfter}
        />
      </PageWidth>
    );
  }

  const used = clampPoints(points, quote.total, wallet.points, wallet.pointValue);
  const cash = Math.max(0, Math.round((quote.total - used * wallet.pointValue) * 100) / 100);

  const confirm = () => {
    if (name.trim().length < 2) {
      setError("Enter the guest name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter an email for the confirmation.");
      return;
    }
    if (mobile.replace(/\D/g, "").length < 10) {
      setError("Enter a mobile number.");
      return;
    }
    setError("");
    setSubmitting(true);
    void saveTrip({
      kind: "stay",
      title: hotel.name,
      subtitle: `${hotel.city} · ${room.name}`,
      image: hotel.image,
      startDate: checkIn,
      endDate: checkOut,
      detail: `${quote.nights} nights · ${guests} adults · ${rooms} room${rooms === 1 ? "" : "s"}${requests.trim() ? ` · ${requests.trim()}` : ""}`,
      travellers: guests,
      totalInr: quote.total,
      pointsUsed: used,
      guest: { name: name.trim(), email: email.trim(), mobile: mobile.trim() },
    })
      .then((result) => {
        setDone({
          reference: result.trip.reference,
          tierBefore: result.tierBefore,
          tierAfter: result.tierAfter,
          cashPaid: result.trip.cashPaid,
          pointsUsed: result.trip.pointsUsed,
          pointsEarned: result.trip.pointsEarned,
          earnPending: result.earnState === "pending",
          earnAvailableAt: result.earnAvailableAt,
        });
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Could not confirm.");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <PageWidth>
      <Link
        to={`/member/hotels/${hotel.id}`}
        className="text-sm font-semibold text-[#006ce4]"
      >
        Back to {hotel.name}
      </Link>
      <h1 className="mt-2 text-2xl font-bold">Confirm your stay</h1>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <section className="rounded-lg border border-[#e7e7e7] bg-white p-5">
            <h2 className="text-lg font-bold">Guest details</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-medium sm:col-span-2">
                Full name
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="mt-1 h-11 w-full rounded-md border border-[#e7e7e7] px-3"
                />
              </label>
              <label className="text-sm font-medium">
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 h-11 w-full rounded-md border border-[#e7e7e7] px-3"
                />
              </label>
              <label className="text-sm font-medium">
                Mobile
                <input
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  className="mt-1 h-11 w-full rounded-md border border-[#e7e7e7] px-3"
                />
              </label>
              <label className="text-sm font-medium sm:col-span-2">
                Requests for the hotel
                <textarea
                  value={requests}
                  onChange={(event) => setRequests(event.target.value)}
                  className="mt-1 min-h-20 w-full rounded-md border border-[#e7e7e7] px-3 py-2"
                  placeholder="Late arrival, extra bed, and similar notes"
                />
              </label>
            </div>
          </section>
          <PointsCashPay
            total={quote.total}
            availablePoints={wallet.points}
            points={used}
            pointValue={wallet.pointValue}
            currency={wallet.currency}
            onPointsChange={setPoints}
          />
          {error ? (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            disabled={submitting}
            onClick={confirm}
            className="h-12 w-full rounded-md bg-[#006ce4] text-base font-semibold text-white hover:bg-[#0057b8] disabled:opacity-60"
          >
            {submitting
              ? "Confirming…"
              : `Pay ${new Intl.NumberFormat("en-IN", { style: "currency", currency: wallet.currency || "AED", maximumFractionDigits: wallet.pointValue < 1 ? 2 : 0 }).format(cash)}${used ? ` + ${used.toLocaleString("en-IN")} pts` : ""}`}
          </button>
          <p className="text-xs text-[#595959]">
            This confirms the stay on your membership. No card is charged in this checkout.
          </p>
        </div>

        <aside className="h-fit rounded-lg border border-[#e7e7e7] bg-white p-4 lg:sticky lg:top-4">
          <img src={hotel.image} alt="" className="h-36 w-full rounded-md object-cover" />
          <h2 className="mt-3 font-bold">{hotel.name}</h2>
          <p className="text-sm text-[#595959]">
            {hotel.city} · {room.name}
          </p>
          <p className="mt-3 text-sm">
            {formatLongDate(checkIn)} – {formatLongDate(checkOut)}
          </p>
          <p className="text-sm text-[#595959]">
            {quote.nights} nights · {guests} adults · {rooms} room
            {rooms === 1 ? "" : "s"}
          </p>
          <dl className="mt-4 space-y-1 border-t border-[#e7e7e7] pt-3 text-sm">
            <div className="flex justify-between">
              <dt>Room</dt>
              <dd>{formatMoney(quote.base, wallet.currency)}</dd>
            </div>
            <div className="flex justify-between text-[#008009]">
              <dt>Member rate</dt>
              <dd>−{formatMoney(quote.discount, wallet.currency)}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Taxes</dt>
              <dd>{formatMoney(quote.taxes, wallet.currency)}</dd>
            </div>
            <div className="flex justify-between font-bold">
              <dt>Total</dt>
              <dd>{formatMoney(quote.total, wallet.currency)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </PageWidth>
  );
}
